import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  CreditCard,
  FileText,
  Building2,
} from 'lucide-react';

import {
  fetchPaymentRequest,
  fetchPaymentMethods,
  processPayment,
  createStripePaymentSession,
  createPayPalOrder,
  initializePaystackPayment
} from '../../services/PaymentService';

type PaymentMethod = {
  id: string;
  name: string;
  type: string;
  isDefault?: boolean;
  details?: {
    last4?: string;
    expiryMonth?: string;
    expiryYear?: string;
  };
};

type PaymentRequest = {
  _id: string;
  amount: number;
  currency: string;
  description: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  paidAt?: string;
  transactionId?: string;
  paymentMethod?: string;
  createdAt: string;
  projectId: string | { _id: string; title: string };
  projectTitle?: string;
  createdBy: string | { _id: string; firstName: string; lastName: string } | null;
};

const PaymentRequestDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState<'stripe' | 'paypal' | 'paystack' | ''>('');

  useEffect(() => {
    if (id) {
      loadPaymentRequest(id);
      loadPaymentMethods();
    }
  }, [id]);

  const loadPaymentRequest = async (requestId: string) => {
    try {
      setLoading(true);
      const request = await fetchPaymentRequest(requestId);
      setPaymentRequest(request);
    } catch (error) {
      setError('Failed to load payment request details');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const methods = await fetchPaymentMethods();
      setPaymentMethods(methods);
    } catch (error) {
    }
  };

  const handlePayment = async () => {
    if (!paymentRequest) return;
    
    // For direct payment methods
    if (selectedPaymentMethod && !paymentProvider) {
      processDirectPayment();
      return;
    }
    
    // For payment providers
    if (paymentProvider) {
      processProviderPayment();
      return;
    }
    
    setError('Please select a payment method or provider');
  };
  
  const processDirectPayment = async () => {
    if (!paymentRequest || !selectedPaymentMethod) return;
    
    setProcessing(true);
    setError('');

    try {
      const result = await processPayment(
        paymentRequest._id,
        selectedPaymentMethod
      );

      if (result.success) {
        setSuccess(true);
        setPaymentRequest((prev) =>
          prev
            ? {
                ...prev,
                status: 'paid',
                paidAt: new Date().toISOString(),
                transactionId: result.transactionId,
                paymentMethod:
                  paymentMethods.find((m) => m.id === selectedPaymentMethod)
                    ?.name || 'Card',
              }
            : null
        );

        setShowPaymentModal(false);

        setTimeout(() => {
          navigate('/dashboard/payments/success', {
            state: {
              amount: paymentRequest.amount,
              currency: paymentRequest.currency,
              description: paymentRequest.description,
              transactionId: result.transactionId,
            },
          });
        }, 2000);
      } else {
        throw new Error('Payment processing failed');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to process payment');
    } finally {
      setProcessing(false);
    }
  };
  
  const processProviderPayment = async () => {
    if (!paymentRequest || !paymentProvider || !user) return;
    
    setProcessing(true);
    setError('');

    try {
      switch (paymentProvider) {
        case 'stripe': {
          const { url } = await createStripePaymentSession(
            paymentRequest._id,
            paymentRequest.amount,
            paymentRequest.currency
          );
          window.location.href = url;
          break;
        }
        
        case 'paypal': {
          const { approvalUrl } = await createPayPalOrder(
            paymentRequest._id,
            paymentRequest.amount,
            paymentRequest.currency
          );
          window.location.href = approvalUrl;
          break;
        }
        
        case 'paystack': {
          const { authorizationUrl } = await initializePaystackPayment(
            paymentRequest._id,
            paymentRequest.amount,
            paymentRequest.currency,
            user.email
          );
          window.location.href = authorizationUrl;
          break;
        }
        
        default:
          throw new Error('Invalid payment provider');
      }
    } catch (error: any) {
      setError(error.message || `Failed to process ${paymentProvider} payment`);
      setProcessing(false);
    }
  };

  const getStatusColor = (status: string) => {
    if (theme === 'dark') {
      switch (status) {
        case 'pending':
          return 'text-yellow-300 bg-yellow-900/30 border-yellow-800';
        case 'paid':
          return 'text-green-300 bg-green-900/30 border-green-800';
        case 'overdue':
          return 'text-red-300 bg-red-900/30 border-red-800';
        case 'cancelled':
          return 'text-gray-300 bg-gray-800 border-gray-700';
        default:
          return 'text-gray-300 bg-gray-800 border-gray-700';
      }
    } else {
      switch (status) {
        case 'pending':
          return 'text-yellow-700 bg-yellow-100 border-yellow-200';
        case 'paid':
          return 'text-green-700 bg-green-100 border-green-200';
        case 'overdue':
          return 'text-red-700 bg-red-100 border-red-200';
        case 'cancelled':
          return 'text-gray-700 bg-gray-100 border-gray-200';
        default:
          return 'text-gray-700 bg-gray-100 border-gray-200';
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5" />;
      case 'paid':
        return <CheckCircle className="w-5 h-5" />;
      case 'overdue':
        return <AlertCircle className="w-5 h-5" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5" />;
      default:
        return <Clock className="w-5 h-5" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getDaysUntilDue = (dueDate: string) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffMs = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const getPaymentMethodIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCard className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} />;
      case 'paypal':
        return <div className="w-6 h-6 text-blue-600 font-bold">P</div>;
      case 'bank':
        return <Building2 className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} />;
      default:
        return <CreditCard className={`w-6 h-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`} />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div
          className={`h-16 ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-gray-200'
          } rounded-lg animate-pulse`}
        ></div>
        <div
          className={`${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          } p-6 rounded-lg shadow-sm border animate-pulse`}
        >
          <div
            className={`h-8 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded w-1/3 mb-4`}
          ></div>
          <div
            className={`h-4 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded w-full mb-2`}
          ></div>
          <div
            className={`h-4 ${
              theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
            } rounded w-2/3`}
          ></div>
        </div>
      </div>
    );
  }

  if (!paymentRequest) {
    return (
      <div className="text-center py-12">
        <AlertCircle
          className={`w-16 h-16 ${
            theme === 'dark' ? 'text-gray-700' : 'text-gray-300'
          } mx-auto mb-4`}
        />
        <h3
          className={`text-lg font-medium ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          } mb-2`}
        >
          Payment request not found
        </h3>
        <p
          className={`${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          } mb-6`}
        >
          The payment request you're looking for doesn't exist.
        </p>
        <Link
          to="/dashboard/payments"
          className={`inline-flex items-center px-4 py-2 ${
            theme === 'dark'
              ? 'bg-blue-600 hover:bg-blue-700'
              : 'bg-blue-600 hover:bg-blue-700'
          } text-white rounded-lg transition-colors duration-200`}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Payments
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/payments"
            className={`p-2 ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            } rounded-lg transition-colors duration-200`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1
              className={`text-2xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}
            >
              Payment Request
            </h1>
            <p
              className={`${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mt-1`}
            >
              Review and process payment
            </p>
          </div>
        </div>

        {paymentRequest.status === 'pending' && (
          <button
            onClick={() => {
              setSelectedPaymentMethod('');
              setPaymentProvider('');
              setShowPaymentModal(true);
            }}
            className={`flex items-center px-4 py-2 ${
              theme === 'dark'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-green-600 hover:bg-green-700'
            } text-white rounded-lg transition-colors duration-200`}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Pay Now
          </button>
        )}
      </div>

      {error && (
        <div
          className={`${
            theme === 'dark'
              ? 'bg-red-900/20 border-red-800'
              : 'bg-red-50 border-red-200'
          } border rounded-lg p-4`}
        >
          <div className="flex items-center">
            <AlertCircle
              className={`w-5 h-5 ${
                theme === 'dark' ? 'text-red-400' : 'text-red-500'
              } mr-2`}
            />
            <span
              className={`${
                theme === 'dark' ? 'text-red-300' : 'text-red-700'
              } text-sm`}
            >
              {error}
            </span>
          </div>
        </div>
      )}

      {success && (
        <div
          className={`${
            theme === 'dark'
              ? 'bg-green-900/20 border-green-800'
              : 'bg-green-50 border-green-200'
          } border rounded-lg p-4`}
        >
          <div className="flex items-center">
            <CheckCircle
              className={`w-5 h-5 ${
                theme === 'dark' ? 'text-green-400' : 'text-green-500'
              } mr-2`}
            />
            <span
              className={`${
                theme === 'dark' ? 'text-green-300' : 'text-green-700'
              } text-sm`}
            >
              Payment processed successfully! Redirecting to payment
              confirmation...
            </span>
          </div>
        </div>
      )}

      {/* Payment Request Details */}
      <div
        className={`${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        } rounded-lg shadow-sm border`}
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <DollarSign
                className={`w-8 h-8 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`}
              />
              <div>
                <h2
                  className={`text-xl font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {paymentRequest.description}
                </h2>
                <p
                  className={`${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Project: {paymentRequest.projectTitle}
                </p>
              </div>
            </div>
            <span
              className={`flex items-center px-3 py-1 text-sm font-medium rounded-full capitalize border ${getStatusColor(
                paymentRequest.status
              )}`}
            >
              {getStatusIcon(paymentRequest.status)}
              <span className="ml-2">{paymentRequest.status}</span>
            </span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Payment Information */}
          <div>
            <h3
              className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}
            >
              Payment Information
            </h3>
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
              } rounded-lg p-4 space-y-4`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p
                    className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Amount
                  </p>
                  <p
                    className={`text-2xl font-bold ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {formatCurrency(
                      paymentRequest.amount,
                      paymentRequest.currency
                    )}
                  </p>
                </div>

                <div>
                  <p
                    className={`text-sm ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}
                  >
                    Due Date
                  </p>
                  <p
                    className={`font-medium ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {formatDate(paymentRequest.dueDate)}
                  </p>
                  <p
                    className={`text-sm ${
                      new Date(paymentRequest.dueDate) < new Date() &&
                      paymentRequest.status !== 'paid'
                        ? theme === 'dark'
                          ? 'text-red-400'
                          : 'text-red-600'
                        : theme === 'dark'
                        ? 'text-gray-400'
                        : 'text-gray-600'
                    }`}
                  >
                    {getDaysUntilDue(paymentRequest.dueDate)}
                  </p>
                </div>
              </div>

              {paymentRequest.status === 'paid' && (
                <div
                  className={`${
                    theme === 'dark'
                      ? 'bg-green-900/20 border-green-800'
                      : 'bg-green-50 border-green-200'
                  } border rounded-lg p-3`}
                >
                  <div className="flex items-center space-x-2">
                    <CheckCircle
                      className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                      }`}
                    />
                    <div>
                      <p
                        className={`font-medium ${
                          theme === 'dark' ? 'text-green-300' : 'text-green-700'
                        }`}
                      >
                        Paid on {formatDate(paymentRequest.paidAt || '')}
                      </p>
                      <p
                        className={`text-sm ${
                          theme === 'dark' ? 'text-green-400' : 'text-green-600'
                        }`}
                      >
                        {paymentRequest.paymentMethod} • Transaction ID:{' '}
                        {paymentRequest.transactionId}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Request Details */}
          <div>
            <h3
              className={`text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              } mb-4`}
            >
              Request Details
            </h3>
            <div
              className={`${
                theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
              } rounded-lg p-4 space-y-3`}
            >
              <div>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Description
                </p>
                <p
                  className={`${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {paymentRequest.description}
                </p>
              </div>

              <div>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Created By
                </p>
                <p
                  className={`${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {typeof paymentRequest.createdBy === 'string' ||
                  !paymentRequest.createdBy
                    ? paymentRequest.createdBy || 'N/A'
                    : `${paymentRequest.createdBy.firstName} ${paymentRequest.createdBy.lastName}`}
                </p>
              </div>

              <div>
                <p
                  className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Created On
                </p>
                <p
                  className={`${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {formatDate(paymentRequest.createdAt)} (
                  {getRelativeTime(paymentRequest.createdAt)})
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div
            className={`flex items-center justify-between pt-6 border-t ${
              theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            <Link
              to="/dashboard/payments"
              className={`${
                theme === 'dark'
                  ? 'text-blue-400 hover:text-blue-300'
                  : 'text-blue-600 hover:text-blue-700'
              } font-medium`}
            >
              Back to Payments
            </Link>

            <Link
              to={`/dashboard/projects/${paymentRequest.projectId}`}
              className={`flex items-center px-4 py-2 ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white rounded-lg transition-colors duration-200`}
            >
              <FileText className="w-4 h-4 mr-2" />
              View Project
            </Link>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div
                className={`absolute inset-0 ${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-gray-500'
                } opacity-75`}
              ></div>
            </div>

            {/* Modal panel */}
            <div
              className={`inline-block align-bottom ${
                theme === 'dark' ? 'bg-gray-800' : 'bg-white'
              } rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full`}
            >
              <div
                className={`${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                } px-4 pt-5 pb-4 sm:p-6 sm:pb-4`}
              >
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3
                      className={`text-lg leading-6 font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      Process Payment
                    </h3>
                    <div className="mt-2">
                      <p
                        className={`text-sm ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}
                      >
                        Select a payment method to pay{' '}
                        {formatCurrency(
                          paymentRequest.amount,
                          paymentRequest.currency
                        )}
                      </p>
                    </div>

                    {error && (
                      <div
                        className={`mt-4 ${
                          theme === 'dark'
                            ? 'bg-red-900/20 border-red-800'
                            : 'bg-red-50 border-red-200'
                        } border rounded-lg p-3`}
                      >
                        <div className="flex items-center">
                          <AlertCircle
                            className={`w-5 h-5 ${
                              theme === 'dark' ? 'text-red-400' : 'text-red-500'
                            } mr-2`}
                          />
                          <span
                            className={`${
                              theme === 'dark' ? 'text-red-300' : 'text-red-700'
                            } text-sm`}
                          >
                            {error}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="mt-6 space-y-4">
                      <div
                        className={`p-4 ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
                        } rounded-lg`}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <h4
                            className={`font-medium ${
                              theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}
                          >
                            Payment Summary
                          </h4>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span
                              className={`${
                                theme === 'dark'
                                  ? 'text-gray-300'
                                  : 'text-gray-700'
                              }`}
                            >
                              Description:
                            </span>
                            <span
                              className={`font-medium ${
                                theme === 'dark'
                                  ? 'text-white'
                                  : 'text-gray-900'
                              }`}
                            >
                              {paymentRequest.description}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span
                              className={`${
                                theme === 'dark'
                                  ? 'text-gray-300'
                                  : 'text-gray-700'
                              }`}
                            >
                              Project:
                            </span>
                            <span
                              className={`font-medium ${
                                theme === 'dark'
                                  ? 'text-white'
                                  : 'text-gray-900'
                              }`}
                            >
                              {paymentRequest.projectTitle}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span
                              className={`${
                                theme === 'dark'
                                  ? 'text-gray-300'
                                  : 'text-gray-700'
                              }`}
                            >
                              Amount:
                            </span>
                            <span
                              className={`font-medium ${
                                theme === 'dark'
                                  ? 'text-white'
                                  : 'text-gray-900'
                              }`}
                            >
                              {formatCurrency(
                                paymentRequest.amount,
                                paymentRequest.currency
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span
                              className={`${
                                theme === 'dark'
                                  ? 'text-gray-300'
                                  : 'text-gray-700'
                              }`}
                            >
                              Due Date:
                            </span>
                            <span
                              className={`font-medium ${
                                theme === 'dark'
                                  ? 'text-white'
                                  : 'text-gray-900'
                              }`}
                            >
                              {formatDate(paymentRequest.dueDate)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Payment Provider Selection */}
                      <div>
                        <label
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          } mb-2`}
                        >
                          Select Payment Provider
                        </label>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <button
                            type="button"
                            onClick={() => {
                              setPaymentProvider('stripe');
                              setSelectedPaymentMethod('');
                            }}
                            className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center transition-all duration-200 ${
                              paymentProvider === 'stripe'
                                ? theme === 'dark'
                                  ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                                  : 'border-blue-500 bg-blue-50 text-blue-700'
                                : theme === 'dark'
                                ? 'border-gray-700 text-gray-300 hover:border-gray-600'
                                : 'border-gray-300 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            <CreditCard className="w-6 h-6 mb-2" />
                            <span className="text-sm font-medium">Stripe</span>
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => {
                              setPaymentProvider('paypal');
                              setSelectedPaymentMethod('');
                            }}
                            className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center transition-all duration-200 ${
                              paymentProvider === 'paypal'
                                ? theme === 'dark'
                                  ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                                  : 'border-blue-500 bg-blue-50 text-blue-700'
                                : theme === 'dark'
                                ? 'border-gray-700 text-gray-300 hover:border-gray-600'
                                : 'border-gray-300 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            <div className="w-6 h-6 mb-2 text-blue-600 font-bold">P</div>
                            <span className="text-sm font-medium">PayPal</span>
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => {
                              setPaymentProvider('paystack');
                              setSelectedPaymentMethod('');
                            }}
                            className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center transition-all duration-200 ${
                              paymentProvider === 'paystack'
                                ? theme === 'dark'
                                  ? 'border-blue-500 bg-blue-900/20 text-blue-400'
                                  : 'border-blue-500 bg-blue-50 text-blue-700'
                                : theme === 'dark'
                                ? 'border-gray-700 text-gray-300 hover:border-gray-600'
                                : 'border-gray-300 text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            <Building2 className="w-6 h-6 mb-2" />
                            <span className="text-sm font-medium">Paystack</span>
                          </button>
                        </div>
                        
                        <div className={`text-center text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mb-4`}>
                          - OR -
                        </div>
                      </div>

                      {/* Saved Payment Methods */}
                      <div>
                        <label
                          className={`block text-sm font-medium ${
                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          } mb-2`}
                        >
                          Use Saved Payment Method
                        </label>
                        <div className="space-y-3">
                          {paymentMethods.length === 0 ? (
                            <div className={`p-4 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'} rounded-lg text-center`}>
                              <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                No saved payment methods available.
                              </p>
                              <Link
                                to="/dashboard/payment-methods"
                                className={`inline-block mt-2 text-sm ${
                                  theme === 'dark'
                                    ? 'text-blue-400 hover:text-blue-300'
                                    : 'text-blue-600 hover:text-blue-700'
                                }`}
                              >
                                + Add a payment method
                              </Link>
                            </div>
                          ) : (
                            paymentMethods.map((method) => (
                              <label
                                key={method.id}
                                className={`flex items-center justify-between p-3 border ${
                                  selectedPaymentMethod === method.id
                                    ? theme === 'dark'
                                      ? 'border-blue-500 bg-blue-900/20'
                                      : 'border-blue-500 bg-blue-50'
                                    : theme === 'dark'
                                    ? 'border-gray-700 hover:border-gray-600'
                                    : 'border-gray-300 hover:border-gray-400'
                                } rounded-lg cursor-pointer transition-colors duration-200`}
                                onClick={() => {
                                  setSelectedPaymentMethod(method.id);
                                  setPaymentProvider('');
                                }}
                              >
                                <div className="flex items-center">
                                  <input
                                    type="radio"
                                    name="paymentMethod"
                                    value={method.id}
                                    checked={selectedPaymentMethod === method.id}
                                    onChange={() => {
                                      setSelectedPaymentMethod(method.id);
                                      setPaymentProvider('');
                                    }}
                                    className={`h-4 w-4 ${
                                      theme === 'dark'
                                        ? 'text-blue-600 focus:ring-blue-500 border-gray-700 bg-gray-700'
                                        : 'text-blue-600 focus:ring-blue-500 border-gray-300'
                                    }`}
                                  />
                                  <div className="ml-3">
                                    <span
                                      className={`font-medium ${
                                        theme === 'dark'
                                          ? 'text-white'
                                          : 'text-gray-900'
                                      }`}
                                    >
                                      {method.name}
                                    </span>
                                    {method.type === 'card' &&
                                      method.details?.last4 && (
                                        <p
                                          className={`text-xs ${
                                            theme === 'dark'
                                              ? 'text-gray-400'
                                              : 'text-gray-500'
                                          }`}
                                        >
                                          Expires {method.details?.expiryMonth}/
                                          {method.details?.expiryYear}
                                        </p>
                                      )}
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  {getPaymentMethodIcon(method.type)}
                                </div>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`${
                  theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'
                } px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <button
                  type="button"
                  onClick={handlePayment}
                  disabled={processing || (!selectedPaymentMethod && !paymentProvider)}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 ${
                    theme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  } text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                    processing || (!selectedPaymentMethod && !paymentProvider)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {processing ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    `Pay ${formatCurrency(
                      paymentRequest.amount,
                      paymentRequest.currency
                    )}`
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className={`mt-3 w-full inline-flex justify-center rounded-md border ${
                    theme === 'dark'
                      ? 'border-gray-700 bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentRequestDetails;
